/// <reference path="../../../../node_modules/@ms/office-online-strings/en-US/transcription-strings.d.ts" />

import * as React from 'react';
import { AppButton } from '../../components';
import { Label, Link, Spinner, SpinnerType } from 'office-ui-fabric-react';
import { RouteComponentProps } from 'react-router-dom';
import { getRecoveringPageStyles, getSpinnerStyles, getRecoveryLinkStyles } from './Recovering.styles';
import { TranscriptionRoutePathNames } from '../../routes';
import { AppLayoutSettings } from '../common.styles';
import { LogEvents } from '../../../utils/LogEvents';
import { RenderToPaneKeys } from '../telemetry/RenderToPaneKeys';
import { DurationMeasureType } from '../../../backend/interfaces/ILogEvent';
import { RecoverFromBackup, SetRecoveredTranscriptionFile, SetShowAuthPopupForRecovery } from '../../actions/RecoveringActions';
import { SetFailureStateAndRouteToFailure, FailureContinuation, RouteToFailureOnAuthPopupFailure } from '../../actions/FailureActions';
import { RecoveryKeys } from '../telemetry/RecoveryKeys';
import { ErrorStringFromException } from '../../../utils/utils';
import { AppAuthenticationProviderContext } from '../../context';
import { ITranscriptionSourceFile } from '../../../backend/interfaces/ITranscriptionSourceFileProvider';
import { HostAppSettings, HostAppSettingName } from '../../../backend/office-js/HostAppSettings';
import { IRealTimeTranscriptionConnection } from '../../../backend/interfaces/IRealTimeTranscriptionConnection';
import { CreateDefaultRealTimeTranscriptionConnection } from '../../../backend/RealTimeTranscriptionConnectionFactory';
import { LogEventNames } from '../../../backend/interfaces/ITelemetryLogger';
import recoveringStore from '../../stores/RecoveringStore';
import { ChangeEvent } from '../../stores/utils';
import { PerformGetAccessTokenCheckAndShowAuthPopupIfNeeded } from '../../actions/CommonActions';
import { AuthPopup } from '../../components/Popups/AuthPopup';

export interface RecoveringState {
  recoveredTranscriptionFile: ITranscriptionSourceFile;
  showAuthPopup: boolean;
}

export class Recovering extends React.PureComponent<RouteComponentProps, RecoveringState> {
  static contextType = AppAuthenticationProviderContext;
  private _isMounted: boolean;

  constructor(props: RouteComponentProps) {
    super(props);
    this._isMounted = false;

    this.state = recoveringStore.getState();
    recoveringStore.on(ChangeEvent.Change, this._onStoreChanged);
  }

  public async componentWillMount() {
    let authSuccessful: boolean = false;
    try {
      authSuccessful = await PerformGetAccessTokenCheckAndShowAuthPopupIfNeeded(this.context, SetShowAuthPopupForRecovery);
    } catch (e) {
      RouteToFailureOnAuthPopupFailure(this.props.history)
    }

    if (authSuccessful) {
      await this._performRecoveryTasks();
    }
  }

  public componentWillUnmount() {
    this._isMounted = false;
  }

  public async componentDidMount() {
    this._isMounted = true;
  }

  public render() {
    const recoveryPageUI = this.state.recoveredTranscriptionFile ? this._getRecoveryCompletedUI() : this._getRecoveryProgressUI();

    return (
      <div>
        {recoveryPageUI}
        {   
          this.state.showAuthPopup ?
          <AuthPopup
              onAuthSucceeded={() => this._onAuthSucceeded() } 
              onAuthFailed={() => this._onAuthFailed() }
              onDialogClosed={() => this._onAuthFailed() } />
          : null
        }
      </div>
    );
  }

  private async _onAuthSucceeded() {
    SetShowAuthPopupForRecovery(false /*showAuthPopup*/);
    await this._performRecoveryTasks();
  }

  private _onAuthFailed() {
    SetShowAuthPopupForRecovery(false /*showAuthPopup*/);
    RouteToFailureOnAuthPopupFailure(this.props.history);
  }

  private _getRecoveryCompletedUI() {
    const styles = getRecoveringPageStyles();

    return (
      <div style={AppLayoutSettings.AppCommonStyles.getPageRootContainersCSSProperties()}>
        <div className={styles.labelContainer}>
          <Label styles={AppLayoutSettings.AppCommonStyles.getPageTopLabelStyles()}>{TranscriptionStrings.FileRecoveryCompletionMessage}</Label>
          <Link styles={getRecoveryLinkStyles()} href={this.state.recoveredTranscriptionFile.permanentAudioUrl} target="_blank">{this.state.recoveredTranscriptionFile.fileName}</Link>
        </div>
        <div className={styles.buttonContainer}>
          <AppButton type={'AppButtonProps'} id="GotItButton" text={TranscriptionStrings.FileRecoveryGotItButtonLabel} onExecute={() => { this._navigateAway("got it clicked") }} />
        </div>
      </div>
    );
  }

  private _getRecoveryProgressUI() {
    const styles = getRecoveringPageStyles();

    return (
      <div style={AppLayoutSettings.AppCommonStyles.getPageRootContainersCSSProperties()}>
        <div>
          <Label styles={AppLayoutSettings.AppCommonStyles.getPageTopLabelStyles()}>{TranscriptionStrings.FileRecoveryUploadingLabel}</Label>
        </div>
        <div className={styles.spinnerContainer}>
          <Spinner type={SpinnerType.large} label={TranscriptionStrings.FileRecoveryUploadingStatus} styles={getSpinnerStyles()} />
        </div>
      </div>
    );
  }

  private async _performRecoveryTasks() {
    try {
      LogEvents.Recovery.Start();
      let recoveredFile: ITranscriptionSourceFile = await RecoverFromBackup(this.props.location.state.audioBackup, this.context);

      // Update recovery page state, if file recovered successfully.
      if (recoveredFile != null) {
        LogEvents.Recovery.End([{key: RecoveryKeys.RecoveredSuccessfully, value: true}]);
        SetRecoveredTranscriptionFile(recoveredFile);
      } else {
        const reason: string = "operation was canceled";
        LogEvents.Recovery.End([{key: RecoveryKeys.RecoveredSuccessfully, value: false}, {key: RecoveryKeys.RecoveryFailureReason, value: reason}]);
        this._navigateAway(reason);
      }
    } catch (e) {
      LogEvents.Recovery.End([{key: RecoveryKeys.RecoveredSuccessfully, value: false}, {key: RecoveryKeys.RecoveryFailureReason, value: ErrorStringFromException(e)}]);
      if (this.props.location.state.audioBackup != null) {
        this.props.location.state.audioBackup.Close();
        this.props.location.state.audioBackup = null;
      }

      // Show Failure page
      SetFailureStateAndRouteToFailure(LogEventNames.Recovery, TranscriptionStrings.FileRecoveryFailedFailureReason, FailureContinuation.None /*failureContinuation*/, this.props.history);
    }
  }

  private _navigateAway(reason: string): void {
    if (HostAppSettings.IsSettingTrue(HostAppSettingName.HasStoredTranscription)) {
      LogEvents.RenderToPane.Start([{ key: RenderToPaneKeys.TriggerReason, value: "Recovery - " + reason }]);
      LogEvents.RenderToPane.BeginDurationMeasure(DurationMeasureType.Normal, RenderToPaneKeys.DurationSecs);
      if (this.props.location.state.audioBackup != null) {
        this.props.location.state.audioBackup.Close();
        this.props.location.state.audioBackup = null;
      }

      this.props.history.replace(TranscriptionRoutePathNames.Loading);
    } else {
      // We want to create a transcription connection here to avoid paying the initialization price later, so it improves the "time to input" metric.
      const realTimeTranscriptionConnection: IRealTimeTranscriptionConnection = CreateDefaultRealTimeTranscriptionConnection();
      this.props.history.replace(TranscriptionRoutePathNames.Landing, { realTimeTranscriptionConnection, audioBackup: this.props.location.state.audioBackup});
    }
  };

  private _onStoreChanged = (): void => {
    if (this._isMounted) {
      this.setState(recoveringStore.getState());
    }
  }
};